import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { TherapyPlanAdministrationApiService } from '@therapist/shared/services/api/therapy-plan-administration-api.service';
import { TherapyPlanListItemDto } from '@therapist/shared/models/therapy-plan/therapy-plan-list-item.dto';
import { TherapyPlanDto } from '@therapist/shared/models/therapy-plan/therapy-plan.dto';
import { TherapyPlanExerciseDto } from '@therapist/shared/models/exercise/therapy-plan-exercise.dto';
import { environment } from '../../../../environments/environment';
import { TherapyPlanCreateDto } from '@therapist/shared/models/therapy-plan/therapy-plan-create.dto';
import { TherapyPlanProgressDto } from '@therapist/shared/models/therapy-plan/therapy-plan-progress.dto';
import { forkJoin } from 'rxjs';
import { PreviousTherapyPlanDto } from '@therapist/shared/models/therapy-plan/previous-therapy-plan.dto';

@Injectable({ providedIn: 'root' })
export class TherapyPlanStateService {

  readonly #therapyPlanApiService = inject(TherapyPlanAdministrationApiService);

  // exercises in the therapy-plan with custom sets and repetitions
  readonly #therapyPlanExercises: WritableSignal<TherapyPlanExerciseDto[]> = signal([]);

  readonly #clientTherapyPlans: WritableSignal<TherapyPlanListItemDto[]> = signal([]);
  readonly #selectedTherapyPlan: WritableSignal<TherapyPlanDto | null> = signal(null);

  readonly previousTherapyPlan: WritableSignal<PreviousTherapyPlanDto | null> = signal(null);
  readonly #isPreviousTherapyPlanApplied: WritableSignal<boolean> = signal(false);

  readonly #isTherapyPlanLoaded: WritableSignal<boolean> = signal(false);
  readonly #therapyPlansForClientId = computed(() => {
    if (this.#clientTherapyPlans().length === 0) {
      return null;
    }

    return this.#clientTherapyPlans().at(0)!.clientId!;
  });

  /** cached therapy plan to add, used when navigating between add steps */
  readonly #cachedTherapyPlanToAdd: WritableSignal<Partial<TherapyPlanCreateDto> | null> = signal(null);

  readonly #therapyPlanProgress: WritableSignal<TherapyPlanProgressDto | null> = signal(null);

  constructor() {
    if (environment.enableStateLogging) {
      window.setInterval(() => {
        console.log('selected therapy plan', this.#selectedTherapyPlan());
        console.log('is therapy plan loaded', this.#isTherapyPlanLoaded());
        console.log('previous therapy plan', this.previousTherapyPlan());
      }, 2500);
    }
  }

  loadTherapyPlanListByClientId(id: string): void {
    this.#therapyPlanApiService
      .getAllByClientId(id)
      .subscribe((data: TherapyPlanListItemDto[]) => this.#clientTherapyPlans.set([...data]));
  }

  addTherapyPlanToClientTherapyPlans(therapyPlan: TherapyPlanListItemDto): void {
    this.#clientTherapyPlans.update(current => [
      therapyPlan,
      ...current
    ]);
  }

  loadTherapyPlanById(id: string): void {
    this.#isTherapyPlanLoaded.set(false);

    forkJoin([
      this.#therapyPlanApiService.getById(id),
      this.#therapyPlanApiService.getProgressByTherapyPlanId(id)
    ]).subscribe(([plan, progress]) => {
      this.#selectedTherapyPlan.set(plan);
      this.#therapyPlanProgress.set(progress);
      this.#isTherapyPlanLoaded.set(true);
    });
  }

  loadPreviousTherapyPlan(clientId: string) {
    this.#therapyPlanApiService
      .loadPreviousTherapyPlan(clientId)
      .subscribe(previousPlan => this.previousTherapyPlan.set(previousPlan));
  }

  setSelectedTherapyPlan(therapyPlan: TherapyPlanDto | null, setIsLoaded = false): void {
    this.#selectedTherapyPlan.set(therapyPlan);

    if (setIsLoaded) {
      this.#isTherapyPlanLoaded.set(true);
    }
  }

  getIsTherapyPlanLoaded(): Signal<boolean> {
    return this.#isTherapyPlanLoaded.asReadonly();
  }

  getClientTherapyPlans(): Signal<TherapyPlanListItemDto[]> {
    return this.#clientTherapyPlans.asReadonly();
  }

  getSelectedTherapyPlan(): Signal<TherapyPlanDto | null> {
    return this.#selectedTherapyPlan.asReadonly();
  }

  addTherapyPlanExercise(exerciseToAdd: TherapyPlanExerciseDto): void {
    this.#therapyPlanExercises.update(current => this.addFn(current, exerciseToAdd));
  }

  addOrUpdateTherapyPlanExercise(exercise: TherapyPlanExerciseDto): void {
    this.#therapyPlanExercises.update(current => this.updateFn(current, exercise));
  }

  removeTherapyPlanExercise(id: string): void {
    this.#therapyPlanExercises.update(current => current.filter(e => e.id !== id));
    this.#therapyPlanExercises.update(current => current.filter(e => e.templateId !== id));
  }

  removeAllTherapyPlanExercises(): void {
    this.#therapyPlanExercises.set([]);
  }

  getTherapyPlanExercises(): Signal<TherapyPlanExerciseDto[]> {
    return this.#therapyPlanExercises.asReadonly();
  }

  /**
   * checks if the therapy plan state is in sync with the given client id
   *
   * @param clientId the client id to check
   * @returns true if the therapy plans in the state are in sync with the given client id, false otherwise
   */
  isTherapyPlanStateInSyncWithClientId(clientId: string): boolean {
    return this.#therapyPlansForClientId() === clientId;
  }

  private readonly addFn = (current: TherapyPlanExerciseDto[], exerciseToAdd: TherapyPlanExerciseDto): TherapyPlanExerciseDto[] => [
    ...current,
    exerciseToAdd
  ];

  private readonly updateFn = (current: TherapyPlanExerciseDto[], exerciseToUpdate: TherapyPlanExerciseDto
  ): TherapyPlanExerciseDto[] => {
    const index = current.findIndex(e => e.templateId === exerciseToUpdate.templateId);
    if (index > -1) {
      const updated = [...current];
      updated[index] = exerciseToUpdate;
      return updated;
    }

    return this.addFn(current, exerciseToUpdate);
  };

  /** Returns the cached TherapyPlan which is saved while filling out the form */
  getCachedTherapyPlanToAdd(): Signal<Partial<TherapyPlanCreateDto> | null> {
    return this.#cachedTherapyPlanToAdd.asReadonly();
  }

  /** Sets the cached TherapyPlan which is saved while filling out the form */
  setCachedTherapyPlanToAdd(therapyPlan: Partial<TherapyPlanCreateDto> | null): void {
    this.#cachedTherapyPlanToAdd.set(therapyPlan);
  }

  getTherapyPlanProgress(): Signal<TherapyPlanProgressDto | null> {
    return this.#therapyPlanProgress.asReadonly();
  }

  getPreviousTherapyPlan(): Signal<PreviousTherapyPlanDto | null> {
    return this.previousTherapyPlan.asReadonly();
  }

  setIsPreviousTherapyPlanApplied(value: boolean) {
    this.#isPreviousTherapyPlanApplied.set(value);
  }

  getIsPreviousTherapyPlanApplied(): Signal<boolean> {
    return this.#isPreviousTherapyPlanApplied.asReadonly();
  }

  removeSelected() {
    this.#selectedTherapyPlan.set(null);
    this.#cachedTherapyPlanToAdd.set(null);
    this.previousTherapyPlan.set(null);
    this.#isTherapyPlanLoaded.set(false);
    this.#isPreviousTherapyPlanApplied.set(false);
  }

  removeAll(): void {
    this.removeAllTherapyPlanExercises();
    this.removeSelected();
    this.#clientTherapyPlans.set([]);
    this.#isTherapyPlanLoaded.set(false);
  }
}
